
import { AskDataMode } from '../../settings/types/AskDataMode';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../../settings/types/ISite';
import {
  ExtensionSafeListEntry,
  IExtensionsSiteSettings,
  ISiteAdminBridgeSettings,
  ISiteAdminSettings,
  ISiteBridgeSettingsForSiteAdmin,
  ISiteSettingsForSiteAdmin,
  ISiteSubscriptionSettings,
  UserQuotaMode,
  UserVisibilityMode } from '../../settings/types/ISiteSettingsForSiteAdmin';
import { SizeFilter } from '../filters/SizeFilter';
import { SiteSettingsErrorCode } from '../helpers/ErrorCodes';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ILongRunningAction } from '../models/ILongRunningAction';
import { Module } from '../Module';
import { CreateSiteRequest, ICreateSiteParams, ICreateSiteResult } from '../ServerApi/requests/CreateSiteRequest';
import { DeleteSitesRequest, IDeleteSitesResult } from '../ServerApi/requests/DeleteSitesRequest';
import {
  GetExtensionsSiteSettingsRequest,
  IGetExtensionsSiteSettingsResult,
} from '../ServerApi/requests/GetExtensionsSiteSettingsRequest';
import { GetSiteBridgeSettingsForSiteAdminRequest } from '../ServerApi/requests/GetSiteBridgeSettingsForSiteAdminRequest';
import {
  GetSiteSettingsForServerAdminRequest,
  IGetSiteSettingsForServerAdminResult,
} from '../ServerApi/requests/GetSiteSettingsForServerAdminRequest';
import {
  GetSiteSettingsForSiteAdminRequest,
  IGetSiteSettingsForSiteAdminParams,
  IGetSiteSettingsForSiteAdminResult,
} from '../ServerApi/requests/GetSiteSettingsForSiteAdminRequest';
import { GetSitesRequest, IGetSitesResult } from '../ServerApi/requests/GetSitesRequest';
import {
  IUpdateExtensionsSiteSettingsParams,
  UpdateExtensionsSiteSettingsRequest,
} from '../ServerApi/requests/UpdateExtensionsSiteSettingsRequest';
import { UpdateSiteAvailabilityRequest } from '../ServerApi/requests/UpdateSiteAvailabilityRequest';
import {
  IUpdateSiteBridgeSettingsForSiteAdminParams,
  UpdateSiteBridgeSettingsForSiteAdminRequest,
} from '../ServerApi/requests/UpdateSiteBridgeSettingsForSiteAdminRequest';
import {
  IUpdateSiteSettingsForServerAdminParams,
  UpdateSiteSettingsForServerAdminRequest,
} from '../ServerApi/requests/UpdateSiteSettingsForServerAdminRequest';
import {
  IUpdateSiteSettingsForSiteAdminParams,
  UpdateSiteSettingsForSiteAdminRequest,
} from '../ServerApi/requests/UpdateSiteSettingsForSiteAdminRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { LongRunningAction } from '../services/LongRunningAction';
import { ErrorReason } from '../services/ServerService';
import { Resource } from './Resource';

export class SiteAvailabilityState {
  static ACTIVE = 'active';
  static SUSPENDED = 'suspended';
  static LOCKED = 'locked'; // importing or exporting
}

export class ExtractEncryptionMode {
  static ENABLED = 'enabled';
  static DISABLED = 'disabled';
  static ENFORCED = 'enforced';
}

export const defaultSubscriptionSettingsForSite: ISiteSubscriptionSettings = {
  subscriptionsEnabled: true,
  subscribeOthersEnabled: true,
  customScheduleSubscriptionForSiteEnabled: false,
  allowSubscriptionAttachments: true,
  serverAllowSubscriptionAttachments: true
};

// Used in the Create Site flow
export const defaultSiteSettingsForServerAdmin: ISiteSettingsForServerAdmin = {
  contentType: 'site',
  id: undefined,
  userCount: 1,
  licensedUserCount: 1,
  adminCount: undefined,
  creatorCount: 0,
  explorerCount: 0,
  viewerCount: 0,
  availability: SiteAvailabilityState.ACTIVE,
  currentLoggedInUserCount: undefined,
  storageQuotaPercentUsed: undefined,
  storageUsedBytes: undefined,
  createdAt: undefined,
  settings: {
    urlName: '',
    name: '',
    autoSuspendRefreshEnabled: true,
    autoSuspendRefreshInactivityWindow: 32,
    storageQuotaEnabled: false,
    storageQuotaBytes: null,
    siteAdminsCanAddUsers: true,
    userQuotaMode: UserQuotaMode.SERVER_LIMIT,
    userQuota: null,
    metricsEnabled: false,
    managedKeychainCountEnabled: false,
    cacheWarmupEnabled: true,
    authoringEnabled: true,
    webEditingEnabled: true,
    editingFlowsEnabled: true,
    schedulingFlowsEnabled: true,
    catalogingOnSiteEnabled: true,
    flowsOnSiteEnabled: true,
    versionHistoryEnabled: true,
    contentVersionLimitEnabled: true,
    contentVersionLimit: 25,
    guestAccessEnabled: false,
    customSubscriptionEmailEnabled: false,
    customSubscriptionEmail: '',
    customSubscriptionFooterEnabled: false,
    customSubscriptionFooter: '',
    defaultStartPage: undefined,
    runNowEnabled: undefined,
    tierCreatorCapacity: null,
    tierExplorerCapacity: null,
    tierViewerCapacity: null,
    dataAccelerationEnabled: true
  },
  subscriptionSettings: defaultSubscriptionSettingsForSite,
};

export const defaultSiteSettings: ISiteAdminSettings = {
  derivedPermissionsEnabled: false,
  refreshTokenEnabled: true,
  notificationEnabled: true,
  addSupportUserEnabled: false,
  remoteQueryingEnabled: false,
  sheetImageEnabled: true,
  siteInviteEmailEnabled: true,
  extractEncryptionMode: ExtractEncryptionMode.DISABLED,
  isAdminDataSourceEnabled: false,
  siteStartPageUri: '',
  siteNamedSharingSettings: {
    namedSharingEnabled: true
  },
  vizRecommendationsEnabled: true,
  vizRecUsernameVisibilityEnabled: true,
  siteSandboxSettings: {
    sandboxEnabled: true,
    sandboxDatasourcesEnabled: true,
    sandboxFlowsEnabled: true,
    sandboxStorageQuotaSet: false,
    sandboxStorageQuota: null
  },
  alternativeFederationEngineEnabled: true,
  askDataMode: AskDataMode.ENABLED_BY_DEFAULT,
  userVisibility: UserVisibilityMode.FULL,
  requestAccessEnabled: true,
  dataAlertsEnabled: true,
  siteCommentingSettings: {
      commentingEnabled: true,
      commentingMentionsEnabled: true
  },
  metricsContentTypeEnabled: true,
  webZoneContentEnabled: true,
  notifySiteAdminsOnThrottleEnabled: true,
  runNowEnabled: null,
  catalogObfuscationEnabled: true,
  timeZone: '',
  useDefaultTimeZone: true,
  publicCollectionsEnabled: true,
};

export const defaultSiteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin = {
  siteId: undefined,
  siteAdminSettings: defaultSiteSettings,
  subscriptionSettings: defaultSubscriptionSettingsForSite,
};

export const defaultUrl: string = 'https://';
export const defaultEntry: ExtensionSafeListEntry = {
  'url': defaultUrl,
  'fullDataAllowed': false,
  'promptNeeded': true
};

export const defaultExtensionsSiteSettings: IExtensionsSiteSettings = {
  extensionsEnabled: true,
  allowSandboxed: true,
  safeList: []
};


export class Sites extends Resource<ISiteSettingsForServerAdmin> {

  public static isExtractEncryptionModeEnabled(extractEncryptionMode: ExtractEncryptionMode): boolean {
    if(extractEncryptionMode === ExtractEncryptionMode.ENABLED) {
      return true;
    }
    return false;
  }

  public static isUserVisibilityLimited(userVisibilityMode: UserVisibilityMode): boolean {
    return userVisibilityMode === UserVisibilityMode.LIMITED;
  }

  public static $inject = [
    '$q',
    '$translate',
    '$timeout',
    'ErrorNotifyingServer',
    'FetcherFactory'
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private $timeout: ng.ITimeoutService,
    private errorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory)
  {
    super(FetcherFactory.getServerResourceFetcher(
      GetSitesRequest,
      (result: IGetSitesResult) => {
        return result.sites.map((site) => {
          return {
            ...site,
            contentType: 'site'
          } as ISiteSettingsForServerAdmin;
        });
      }
    ));
  }

  public getSiteSettingsForServerAdminById(id: string): ng.IPromise<IGetSiteSettingsForServerAdminResult> {
    return this.errorNotifyingServer.sendRequest(new GetSiteSettingsForServerAdminRequest({ id }));
  }

  public getSiteSettingsForSiteAdminById(id: string): ng.IPromise<IGetSiteSettingsForSiteAdminResult> {
    const params: IGetSiteSettingsForSiteAdminParams = {
      id: id
    };
    return this.errorNotifyingServer.sendRequest(new GetSiteSettingsForSiteAdminRequest(params));
  }

  public getSiteBridgeSettingsForSiteAdminById(id: string): ng.IPromise<ISiteBridgeSettingsForSiteAdmin> {
    return this.errorNotifyingServer.sendRequest(new GetSiteBridgeSettingsForSiteAdminRequest({ id }));
  }

  public getCurrentExtensionsSiteSettingsForSiteAdmin(): ng.IPromise<IGetExtensionsSiteSettingsResult> {
    return this.errorNotifyingServer.sendRequest(new GetExtensionsSiteSettingsRequest({path: ''}));
  }

  public getCurrentSiteSettingsForServerAdmin(): ng.IPromise<ISiteSettingsForServerAdmin> {
    return this.getSiteSettingsForServerAdminById(this.errorNotifyingServer.getServerService().sessionSiteId());
  }

  public getCurrentSiteSettingsForSiteAdmin(): ng.IPromise<IGetSiteSettingsForSiteAdminResult> {
    return this.getSiteSettingsForSiteAdminById(this.errorNotifyingServer.getServerService().sessionSiteId());
  }

  public getCurrentSiteBridgeSettingsForSiteAdmin(): ng.IPromise<ISiteBridgeSettingsForSiteAdmin> {
    return this.getSiteBridgeSettingsForSiteAdminById(this.errorNotifyingServer.getServerService().sessionSiteId());
  }

  private updateSiteAvailability(ids: string[], availability: string): ng.IPromise<IResult> {
    return this.errorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new UpdateSiteAvailabilityRequest({
        ids: idsBlock,
        availability: availability
      })),
      ResultHelper.combineResults);
  }

  public suspend(ids: string[]): ng.IPromise<IResult> {
    return this.updateSiteAvailability(ids, SiteAvailabilityState.SUSPENDED);
  }

  public activate(ids: string[]): ng.IPromise<IResult> {
    return this.updateSiteAvailability(ids, SiteAvailabilityState.ACTIVE);
  }

  public updateSiteSettingsForServerAdmin(id: string, settings: IServerAdminSettings): ng.IPromise<IResult> {
    const params: IUpdateSiteSettingsForServerAdminParams = {
      id: id,
      settings: settings
    };
    return this.errorNotifyingServer.sendRequest(new UpdateSiteSettingsForServerAdminRequest(params));
  }

  public updateSiteSettingsForSiteAdmin(id: string, siteAdminSettings: ISiteAdminSettings, subscriptionSettings?: ISiteSubscriptionSettings): ng.IPromise<IResult> {
      const params: IUpdateSiteSettingsForSiteAdminParams = {
          id: id,
          siteAdminSettings: siteAdminSettings
      };
      if (subscriptionSettings) {
        params.subscriptionSettings = subscriptionSettings;
      }

      return this.errorNotifyingServer.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params));
  }

  public updateSiteBridgeSettingsForSiteAdmin(id: string, siteAdminBridgeSettings: ISiteAdminBridgeSettings): ng.IPromise<IResult> {
      const params: IUpdateSiteBridgeSettingsForSiteAdminParams = {
          id: id,
          siteAdminBridgeSettings: siteAdminBridgeSettings
      };

      return this.errorNotifyingServer.sendRequest(new UpdateSiteBridgeSettingsForSiteAdminRequest(params));
  }

  public updateCurrentExtensionsSiteSettingsForSiteAdmin(extensionSettings: IExtensionsSiteSettings): ng.IPromise<IResult> {
    const params: IUpdateExtensionsSiteSettingsParams = {
      siteSettings: extensionSettings
    };
    return this.errorNotifyingServer.sendRequest(new UpdateExtensionsSiteSettingsRequest(params));
  }


  public createSite(settings: IServerAdminSettings, siteAdminSettings?: ISiteAdminSettings, subscriptionSettings?: ISiteSubscriptionSettings): ng.IPromise<void> {
    const params: ICreateSiteParams = {
      settings: settings
    };

    if (siteAdminSettings) {
      params.siteAdminSettings = siteAdminSettings;
    }

    if (subscriptionSettings) {
      params.subscriptionSettings = subscriptionSettings;
    }
    return this.errorNotifyingServer.sendRequestAndDelay(new CreateSiteRequest(params))
      .then<void>((result: ICreateSiteResult) => {
        if (result.errors) {
          return this.$q.reject(result.errors);
        }
      });
  }

  public del(ids: string[]): ng.IPromise<any> {
    // Note: We don't send multiple "delete sites" requests because the result is a long running action,
    // and tracking more than one long running action at once would be complicated.
    // Since we don't, attempts to delete more than the server-allowed maximum will fail.
    // This isn't so bad, because the maximum is 1000, and that's a *lot* of sites
    // to try to delete all at once.  It's likely the server can't handle that anyway.
    return this.errorNotifyingServer.sendRequest(new DeleteSitesRequest({ ids: ids })).then((result: IDeleteSitesResult) => {
      if (result.actionId) {
        return LongRunningAction.trackAsyncJobProgress(result.actionId).promise;
      } else {
        return this.$q.reject(ErrorReason.Unknown);
      }
    }).then((action: ILongRunningAction) => this.$timeout(() => action, ErrorNotifyingServer.ResponseDelay));
  }

  public errorMessage(code: SiteSettingsErrorCode, site: ISiteSettingsForServerAdmin): string {
    switch (code) {
      case SiteSettingsErrorCode.SiteNotFound:
        return this.$translate.instant('siteSettingsFailedSiteNotFound');
      case SiteSettingsErrorCode.SiteNameIsEmptyOrNull:
        return this.$translate.instant('siteSettingsFailedSiteNameIsEmptyOrNull');
      case SiteSettingsErrorCode.SiteAdminCannotAddUsersButUserQuotaIsSet:
        return this.$translate.instant('siteSettingsFailedSiteAdminCannotAddUsersButUserQuotaIsSet');
      case SiteSettingsErrorCode.InvalidUserQuotaOrStorageQuota:
        return this.$translate.instant('siteSettingsFailedInvalidUserQuotaOrStorageQuota');
      case SiteSettingsErrorCode.UserQuotaGreaterThanServerLicenseLimit:
        return this.$translate.instant('siteSettingsFailedUserQuotaGreaterThanServerLicenseLimit');
      case SiteSettingsErrorCode.InvalidCustomEmailAddress:
        return this.$translate.instant('siteSettingsFailedInvalidCustomEmailAddress');
      case SiteSettingsErrorCode.SiteNameAlreadyExists:
        return this.$translate.instant('siteSettingsFailedSiteNameOrUrlNameAlreadyExists');
      case SiteSettingsErrorCode.SiteUrlNameAlreadyExists:
        return this.$translate.instant('siteSettingsFailedSiteNameOrUrlNameAlreadyExists');
      case SiteSettingsErrorCode.SiteSubscriptionsSetWithDisabledServerSubscriptions:
        return this.$translate.instant('siteSettingsFailedSiteSubscriptionsSetWithDisabledServerSubscriptions');
      case SiteSettingsErrorCode.AttemptedToUpdateDefaultSiteUrlName:
        return this.$translate.instant('siteSettingsFailedAttemptedToUpdateDefaultSiteUrlName');
      case SiteSettingsErrorCode.AttemptedToSuspendDefaultSite:
        return this.$translate.instant('siteSettingsFailedAttemptedToSuspendDefaultSite');
      case SiteSettingsErrorCode.SiteNameTooLong:
        return this.$translate.instant('siteSettingsFailedSiteNameTooLong');
      case SiteSettingsErrorCode.SiteUrlNameTooLong:
        return this.$translate.instant('siteSettingsFailedSiteUrlNameTooLong');
      case SiteSettingsErrorCode.StorageQuotaLessThanStorageUsed: // server admin - requires 'site' parameter
        return this.$translate.instant('siteSettingsFailedStorageQuotaLessThanStorageUsed', {
          STORAGE_USED : SizeFilter(site.storageUsedBytes)
        });
      case SiteSettingsErrorCode.SupportUsersOwnContent:
        return this.$translate.instant('siteSettingsFailedSupportUsersOwnContent');
      default:
        return this.$translate.instant('settingsUpdateFailure');
    }
  }

}

Module.instance().service('Sites', Sites);
